import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import Title from "../../extra/Title";
import { apiInstanceFetch } from "../../../util/api";
import defaultImage from "../../../assets/images/default.jpg";

const INK = "#1A1614";
const MUTED = "#8A857F";
const HAIRLINE = "#E8E1D3";
const GOLD = "#D4A129";
const CREAM = "#FFFDF7";
const RED = "#DC2626";
const GREEN = "#16A34A";

// Phase-2 B — self-serve advertising console. Campaigns are funded with
// coins (1000 ≈ $1); rejecting a pending campaign auto-refunds the
// advertiser's unspent budget.
const STATUSES = ["pending_review", "active", "paused", "completed", "rejected"];
const statusLabel = (s) => ({
  pending_review: "Pending",
  active: "Active",
  paused: "Paused",
  completed: "Completed",
  rejected: "Rejected",
}[s] || s);

const AdsReview = () => {
  const [status, setStatus] = useState("pending_review");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState("");
  const [rejectFor, setRejectFor] = useState(null);
  // Product-link picker: which campaign is being linked, plus search state.
  const [linkProductFor, setLinkProductFor] = useState(null);
  const [productQuery, setProductQuery] = useState("");
  const [productResults, setProductResults] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const load = () =>
    apiInstanceFetch
      .get(`adCampaign/admin/list?status=${status}&page=1&limit=200`)
      .then((r) => {
        if (r?.status) {
          setRows(r.data || []);
          setTotal(r.total || 0);
        }
      })
      .catch(() => {});

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [status]);

  // Ads are reviewed here anyway, so attaching the product during review is
  // the natural moment. Campaigns created before ads supported products (and
  // any advertiser who skipped the picker) are dead on tap until this is set,
  // and there is no edit-ad screen for them.
  const searchProducts = (q) => {
    setProductLoading(true);
    apiInstanceFetch
      .get(`adCampaign/admin/productSearch?q=${encodeURIComponent(q)}&limit=25`)
      .then((r) => setProductResults(r?.status ? r.products || [] : []))
      .catch(() => setProductResults([]))
      .finally(() => setProductLoading(false));
  };

  const setProduct = (campaignId, productId) => {
    setBusy(campaignId);
    apiInstanceFetch
      .post(`adCampaign/admin/setProduct`, { campaignId, productId: productId || "" })
      .then((r) => {
        if (r?.status) {
          setRows((prev) =>
            prev.map((x) =>
              x._id === campaignId ? { ...x, productId: r.campaign?.productId ? productId : null, productName: undefined } : x
            )
          );
          load();
          setLinkProductFor(null);
        } else {
          alert(r?.message || "Could not link product");
        }
      })
      .catch(() => alert("Could not link product"))
      .finally(() => setBusy(null));
  };

  const decide = (campaign, decision, reason) => {
    setBusy(campaign._id);
    apiInstanceFetch
      .post(`adCampaign/admin/decision`, {
        campaignId: campaign._id,
        decision,
        ...(reason ? { reason } : {}),
      })
      .then((r) => {
        if (r?.status) setRows((prev) => prev.filter((x) => x._id !== campaign._id));
        else alert(r?.message || "Action failed");
      })
      .catch(() => alert("Action failed"))
      .finally(() => setBusy(""));
  };

  const advertiser = (r) => {
    const u = r.advertiserUserId || {};
    return `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Unknown";
  };

  return (
    <div className="mainCategory">
      <Title name="Ads — Campaigns" />

      <div className="d-flex align-items-center flex-wrap" style={{ gap: 8, margin: "16px 0" }}>
        <i className="fa-solid fa-rectangle-ad" style={{ color: GOLD }} />
        <span style={{ fontWeight: 700, color: INK, fontSize: 15 }}>
          {statusLabel(status)} ({total})
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className="btn btn-sm"
              style={{
                border: `1px solid ${status === s ? INK : HAIRLINE}`,
                background: status === s ? INK : "#FFF",
                color: status === s ? "#FFF" : INK,
                fontWeight: 600,
              }}
            >
              {statusLabel(s)}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div style={{ background: "#fff", border: `1px solid ${HAIRLINE}`, borderRadius: 14, padding: 48, textAlign: "center", color: MUTED }}>
          <i className="fa-solid fa-rectangle-ad" style={{ fontSize: 34, color: HAIRLINE }} />
          <div style={{ marginTop: 12, fontWeight: 600 }}>No {statusLabel(status).toLowerCase()} campaigns</div>
        </div>
      ) : (
        <div style={{ background: "#fff", border: `1px solid ${HAIRLINE}`, borderRadius: 14, overflow: "auto" }}>
          <table className="table align-middle" style={{ margin: 0, minWidth: 900 }}>
            <thead>
              <tr style={{ background: CREAM }}>
                {["Media", "Title", "Advertiser", "Format / Slot", "Product", "Budget", "Spent", "Views", "Created", "Action"].map((h) => (
                  <th key={h} style={{ fontSize: 11, textTransform: "uppercase", color: MUTED, padding: "10px 12px", borderBottom: `2px solid ${HAIRLINE}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r._id}>
                  <td style={{ padding: "8px 12px" }}>
                    <a href={r.media} target="_blank" rel="noreferrer" title="Open media">
                      {r.format === "image" ? (
                        <img src={r.media} alt="" style={{ width: 64, height: 40, objectFit: "cover", borderRadius: 6 }}
                          onError={(e) => (e.currentTarget.src = defaultImage)} />
                      ) : (
                        <span style={{ display: "inline-block", background: INK, color: "#fff", borderRadius: 6, padding: "8px 12px", fontSize: 12, fontWeight: 700 }}>
                          <i className="fa-solid fa-play" style={{ marginRight: 6 }} />Video
                        </span>
                      )}
                    </a>
                  </td>
                  <td style={{ padding: "8px 12px", maxWidth: 220 }}>
                    <div style={{ fontWeight: 600, color: INK }}>{r.title}</div>
                    {r.rejectionReason ? <div style={{ fontSize: 11.5, color: RED }}>Rejected: {r.rejectionReason}</div> : null}
                  </td>
                  <td style={{ padding: "8px 12px" }}>{advertiser(r)}</td>
                  <td style={{ padding: "8px 12px", textTransform: "capitalize" }}>{r.format} · {r.slot === "watch_earn" ? "Watch & Earn" : r.slot}</td>
                  <td style={{ padding: "8px 12px", maxWidth: 200 }}>
                    {r.productId ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 12, color: INK, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 120 }}>
                          {r.productId.productName || "Linked"}
                        </span>
                        <button
                          disabled={busy === r._id}
                          onClick={() => setProduct(r._id, "")}
                          title="Unlink product"
                          className="btn btn-sm"
                          style={{ background: "#FDECEC", color: RED, border: "none", fontSize: 11, fontWeight: 700, padding: "2px 7px" }}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <button
                        disabled={busy === r._id}
                        onClick={() => { setLinkProductFor(r._id); setProductQuery(""); setProductResults([]); searchProducts(""); }}
                        className="btn btn-sm"
                        style={{ background: "#FFF6DA", color: GOLD, border: "none", fontSize: 11.5, fontWeight: 700 }}
                      >
                        Link product
                      </button>
                    )}
                  </td>
                  <td style={{ padding: "8px 12px" }}>{(r.budgetCoins || 0).toLocaleString()} 🪙</td>
                  <td style={{ padding: "8px 12px" }}>{(r.spentCoins || 0).toLocaleString()} 🪙</td>
                  <td style={{ padding: "8px 12px" }}>{r.format === "image" ? (r.impressionsDelivered || 0) : (r.viewsDelivered || 0)}</td>
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>{dayjs(r.createdAt).format("DD MMM YYYY")}</td>
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                    {r.status === "pending_review" ? (
                      <div style={{ display: "flex", gap: 6 }}>
                        <button disabled={busy === r._id} onClick={() => decide(r, "approved")}
                          className="btn btn-sm" style={{ background: "#E7F6EC", color: GREEN, fontWeight: 700, border: "none" }}>
                          Approve
                        </button>
                        <button disabled={busy === r._id} onClick={() => { setRejectFor(r._id); setRejectReason(""); }}
                          className="btn btn-sm" style={{ background: "#FDECEC", color: RED, fontWeight: 700, border: "none" }}>
                          Reject
                        </button>
                      </div>
                    ) : (
                      <span style={{ fontSize: 12, color: MUTED, textTransform: "capitalize" }}>{statusLabel(r.status)}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {linkProductFor ? (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050 }}>
          <div style={{ background: "#fff", borderRadius: 14, padding: 22, width: 520, maxWidth: "94%" }}>
            <div style={{ fontWeight: 700, color: INK, marginBottom: 4 }}>Link a product to this ad</div>
            <div style={{ fontSize: 12.5, color: MUTED, marginBottom: 12 }}>
              Shoppers who tap the banner will open this product in the app.
            </div>
            <input
              autoFocus
              value={productQuery}
              onChange={(e) => { setProductQuery(e.target.value); searchProducts(e.target.value); }}
              placeholder="Search products by name…"
              style={{ width: "100%", border: `1px solid ${HAIRLINE}`, borderRadius: 10, padding: "9px 12px", outline: "none", marginBottom: 12 }}
            />
            <div style={{ maxHeight: 320, overflow: "auto", border: `1px solid ${HAIRLINE}`, borderRadius: 10 }}>
              {productLoading ? (
                <div style={{ padding: 18, textAlign: "center", color: MUTED, fontSize: 13 }}>Searching…</div>
              ) : productResults.length === 0 ? (
                <div style={{ padding: 18, textAlign: "center", color: MUTED, fontSize: 13 }}>No products found.</div>
              ) : (
                productResults.map((p) => (
                  <div
                    key={p._id}
                    onClick={() => setProduct(linkProductFor, p._id)}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderBottom: `1px solid ${HAIRLINE}`, cursor: "pointer" }}
                  >
                    <img
                      src={p.mainImage || defaultImage}
                      alt=""
                      onError={(e) => { e.target.src = defaultImage; }}
                      style={{ width: 38, height: 38, borderRadius: 8, objectFit: "cover" }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {p.productName}
                      </div>
                      <div style={{ fontSize: 11.5, color: MUTED }}>
                        {p.createStatus !== "Approved" ? `${p.createStatus} · ` : ""}${p.price}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
              <button onClick={() => setLinkProductFor(null)} className="btn btn-sm" style={{ background: CREAM, color: INK, border: `1px solid ${HAIRLINE}`, fontWeight: 600 }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {rejectFor ? (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050 }}>
          <div style={{ background: "#fff", borderRadius: 14, padding: 22, width: 420, maxWidth: "92%" }}>
            <h5 style={{ marginTop: 0 }}>Reject campaign</h5>
            <p style={{ fontSize: 12.5, color: MUTED }}>The advertiser's unspent coins are refunded automatically.</p>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Reason (shown to the advertiser)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <div className="d-flex justify-content-end" style={{ gap: 8, marginTop: 14 }}>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setRejectFor(null)}>Cancel</button>
              <button
                className="btn btn-sm"
                style={{ background: RED, color: "#fff", fontWeight: 700 }}
                disabled={!rejectReason.trim()}
                onClick={() => {
                  const row = rows.find((x) => x._id === rejectFor);
                  decide(row, "rejected", rejectReason.trim());
                  setRejectFor(null);
                }}
              >
                Reject & refund
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdsReview;
